import React, { useMemo } from "react";
import { Loader2, TrendingDown, Trophy } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";

import { BrazilDate } from "../../../utils/BrazilDate";
import { useRelatorioSabores } from "../context/RelatorioSaboresContext";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import type { TopSaborNoPeriodo } from "../utils/relatorioSaboresChartData";

type Opt = { value: number; label: string };

const TAMANHO_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  Opt,
  false
>;

const TOP10_PARTES_OPTIONS: Opt[] = [
  { value: 1, label: "1/1 — inteira (um sabor)" },
  { value: 2, label: "1/2 — meio a meio" },
  { value: 3, label: "1/3 — três sabores" },
  { value: 4, label: "1/4 — quatro sabores" },
];

function SaboresRankingTable({ rows }: { rows: TopSaborNoPeriodo[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-100">
      <table className="w-full min-w-[320px] text-left text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50/90">
            <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
              #
            </th>
            <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
              Sabor
            </th>
            <th className="px-3 py-2.5 text-right font-semibold text-neutral-800 sm:px-4">
              Quantidade
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.saborId}
              className="border-b border-neutral-100 last:border-b-0"
            >
              <td className="px-3 py-2.5 font-medium tabular-nums text-neutral-600 sm:px-4">
                {row.rank}
              </td>
              <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                {row.saborNome}
              </td>
              <td className="px-3 py-2.5 text-right font-medium tabular-nums text-neutral-900 sm:px-4">
                {row.quantidade}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PizzasPorSaborTop10Block: React.FC = () => {
  const {
    top10SaboresNoPeriodo,
    bottom10SaboresNoPeriodo,
    isLoadingTop10Periodo,
    appliedFilters,
    allTamanhoOptions,
    top10TamanhoId,
    setTop10TamanhoId,
    top10Partes,
    setTop10Partes,
  } = useRelatorioSabores();

  const tamanhoSel = useMemo(
    () => allTamanhoOptions.find((o) => o.value === top10TamanhoId) ?? null,
    [allTamanhoOptions, top10TamanhoId]
  );

  const partesSel = useMemo(
    () => TOP10_PARTES_OPTIONS.find((o) => o.value === top10Partes) ?? null,
    [top10Partes]
  );

  const resumoFiltroTop10 = useMemo(() => {
    if (!top10TamanhoId && top10Partes == null) {
      return "Todos os tamanhos e todas as proporções de sabor (1/1 a 1/4).";
    }
    const partesFrase =
      top10Partes != null
        ? (TOP10_PARTES_OPTIONS.find((o) => o.value === top10Partes)?.label ??
          `1/${top10Partes}`)
        : "qualquer proporção";
    const tamanhoLabel =
      top10TamanhoId != null
        ? (allTamanhoOptions.find((o) => o.value === top10TamanhoId)?.label ??
          `Tamanho #${top10TamanhoId}`)
        : "todos os tamanhos";
    return `Filtrando por ${tamanhoLabel} e ${partesFrase}. Loja, sabores do relatório principal e pizzaiolo não alteram estes rankings.`;
  }, [top10TamanhoId, top10Partes, allTamanhoOptions]);

  if (!appliedFilters?.dataInicio || !appliedFilters?.dataFim) {
    return null;
  }

  const pIni = BrazilDate.formatBR(
    BrazilDate.fromISOString(appliedFilters.dataInicio),
    false
  );
  const pFim = BrazilDate.formatBR(
    BrazilDate.fromISOString(appliedFilters.dataFim),
    false
  );

  return (
    <section
      className="rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-6"
      aria-label="Top 10 sabores mais e menos vendidos no período"
    >
      <div className="mb-4 flex flex-wrap items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-white">
          <Trophy size={22} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-neutral-900">
            Top 10 — mais e menos vendidos
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            Período <span className="font-medium">{pIni}</span> a{" "}
            <span className="font-medium">{pFim}</span>. {resumoFiltroTop10}
          </p>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neutral-700">
            Tamanho (rankings)
          </span>
          <ReactSelect<Opt, false>
            inputId="top10-tamanho"
            value={tamanhoSel}
            onChange={(o: SingleValue<Opt>) =>
              setTop10TamanhoId(o?.value ?? null)
            }
            options={allTamanhoOptions}
            isClearable
            placeholder="Todos os tamanhos"
            styles={TAMANHO_SELECT_STYLES}
            className="text-sm"
            noOptionsMessage={() => "Nenhum tamanho cadastrado"}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neutral-700">
            Proporção / sabores na pizza
          </span>
          <ReactSelect<Opt, false>
            inputId="top10-partes"
            value={partesSel}
            onChange={(o: SingleValue<Opt>) => setTop10Partes(o?.value ?? null)}
            options={TOP10_PARTES_OPTIONS}
            isClearable
            placeholder="Todas as proporções"
            styles={TAMANHO_SELECT_STYLES}
            className="text-sm"
          />
        </div>
      </div>

      {isLoadingTop10Periodo ? (
        <div className="flex items-center justify-center gap-2 py-14 text-sm text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando rankings…
        </div>
      ) : top10SaboresNoPeriodo.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-500">
          Nenhuma produção finalizada com sabores neste período para a
          combinação selecionada.
        </p>
      ) : (
        <div className="flex flex-col gap-10">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Trophy size={18} strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">
                  Mais vendidos
                </h3>
                <p className="text-xs text-neutral-500">
                  Maior volume agregado por sabor no recorte.
                </p>
              </div>
            </div>
            <SaboresRankingTable rows={top10SaboresNoPeriodo} />
          </div>
          <div>
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-900">
                <TrendingDown size={18} strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">
                  Menos vendidos
                </h3>
                <p className="text-xs text-neutral-500">
                  Menor volume entre os sabores que tiveram produção neste
                  período e filtros.
                </p>
              </div>
            </div>
            <SaboresRankingTable rows={bottom10SaboresNoPeriodo} />
          </div>
        </div>
      )}
    </section>
  );
};

export default PizzasPorSaborTop10Block;
